import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, Fragment as _Fragment, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, withModifiers as _withModifiers } from "vue"

import { ref, computed, watch } from 'vue'
import {
  RolesService,
  Role,
  User,
  CardIssuePointsService,
  ControlQuestionsService,
  MunicipalEntitiesService,
  RoleType,
  Permissions,
  Id,
  AppAuthService,
  UserStatus,
} from '@prodcard/core'
import { createCtx, handleError, useWatcherSavedRef } from '../../../../helpers'
import { useVuelidate, ErrorObject } from '@vuelidate/core'
import { required, requiredIf, email, helpers } from '@vuelidate/validators'
import {
  VPasswordField,
  VCardIssuePointSelect,
  VPhoneField,
  VMunicipalEntitiesSelect,
  DeleteConfirmationDialog,
} from '../../../../components'
import { useAuthPermissions } from '../../../../mixins'
import { UserOpts } from '../interfaces'

interface Props {
  user?: User
  authService: AppAuthService
  rolesService: RolesService
  cardIssuePointsService: CardIssuePointsService
  controlQuestionsService: ControlQuestionsService
  municipalEntitiesService: MunicipalEntitiesService
  disabled?: boolean
  loading?: boolean
}


export default /*#__PURE__*/_defineComponent({
  __name: 'UsersForm',
  props: {
    user: {},
    authService: {},
    rolesService: {},
    cardIssuePointsService: {},
    controlQuestionsService: {},
    municipalEntitiesService: {},
    disabled: { type: Boolean, default: false },
    loading: { type: Boolean, default: false }
  },
  emits: ["submit", "delete"],
  setup(__props: any, { emit }) {

const props = __props;





const { currentMunicipalEntityId, hasPermissions } = useAuthPermissions(props.authService)
const hasManage = computed(() => {
  return hasPermissions(Permissions.CITIZENS_MANAGE)
})
const hasMeManage = computed(() => {
  return hasPermissions(Permissions.ME_CITIZENS_MANAGE)
})
const deleteConfirmation = ref(false)
const roles = ref<Role[]>([])
const loadingRoles = ref(false)
const loading = ref(props.loading)

handleError(async () => {
  const response = await props.rolesService.findAll(createCtx())
  roles.value = response

  if (hasMeManage.value && !hasManage.value && !userOpts.value.roleId) {
    const operator = response.find(r => r.type === RoleType.OPERATOR)
    userOpts.value.roleId = operator?.id ?? ''
  }
}, loadingRoles)

const requiredPassword = (isEdit: boolean) => {
  if (isEdit) return true
  return helpers.withMessage('Пароль не может быть пустым', required)
}

const rules = computed(() => ({
  email: {
    required: helpers.withMessage('Email не может быть пустым', required),
    email: helpers.withMessage('Email некорректен', email),
  },
  password: {
    requiredPassword: requiredPassword(props?.user !== undefined),
  },
  surname: {
    required: helpers.withMessage('Фамилия не может быть пустой', required),
  },
  name: {
    required: helpers.withMessage('Имя не может быть пустым', required),
  },
  roleId: {
    required: helpers.withMessage('Роль не может быть пустой', required),
  },
  municipalEntityId: {
    requiredIf: helpers.withMessage(
      'Муниципальное образование не может быть пустым',
      requiredIf(() => !selectedAdminRole.value),
    ),
  },
  cardIssuePointId: {
    requiredIf: helpers.withMessage(
      'Пункт выдачи карт не может быть пустым',
      requiredIf(() => !!visibleCardIssuePointSelect.value),
    ),
  },
  phone: {
    phone: helpers.withMessage('Контактный телефон некорректен', () => {
      if (!userOpts.value.phone) return true

      return userOpts.value.phone.length === 11
    }),
  },
}))

const userOpts = useWatcherSavedRef<UserOpts>('UserForm', {
  ...props.user,
  status: UserStatus.ACTIVE,
  municipalEntityId:
    hasMeManage.value && !hasManage.value
      ? props.user?.municipalEntityId ?? currentMunicipalEntityId.value
      : props.user?.municipalEntityId,
})

const visibleCardIssuePointSelect = computed(() => {
  const selectedRole = roles.value.find(role => role.id === userOpts.value.roleId)
  return selectedRole && selectedRole.type === RoleType.OPERATOR
})

const validator = useVuelidate<UserOpts>(rules, userOpts)

const submit = async () => {
  const valid = await validator.value.$validate()

  if (valid) emit('submit', userOpts.value)
}

const getErrorMessages = (errors: ErrorObject[]) => {
  return errors.map((e: ErrorObject) => e.$message).join()
}

const selectedAdminRole = computed(() => roles.value.find(r => r.id === userOpts.value.roleId)?.type === RoleType.ADMIN)

watch(
  () => [userOpts.value.municipalEntityId, userOpts.value.roleId],
  () => {
    userOpts.value.cardIssuePointId = undefined
  },
)

watch(
  () => selectedAdminRole.value,
  val => {
    if (val) userOpts.value.municipalEntityId = undefined
  },
)

return (_ctx: any,_cache: any) => {
  const _component_v_select = _resolveComponent("v-select")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_form = _resolveComponent("v-form")!

  return (_openBlock(), _createBlock(_component_v_form, {
    onSubmit: _withModifiers(submit, ["prevent"]),
    disabled: loading.value || _ctx.disabled
  }, {
    default: _withCtx(() => [
      _createVNode(_component_v_row, null, {
        default: _withCtx(() => [
          _createVNode(_component_v_col, {
            cols: "12",
            md: "6"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_select, {
                label: "Роль*",
                "model-value": _unref(userOpts).roleId || undefined,
                items: roles.value,
                loading: loadingRoles.value,
                "item-title": "name",
                "item-value": "id",
                "no-data-text": "Нет данных",
                disabled: loading.value || _ctx.disabled,
                readonly: hasMeManage.value && !hasManage.value,
                clearable: hasMeManage.value && hasManage.value,
                "error-messages": getErrorMessages(_unref(validator).roleId.$errors),
                "onUpdate:modelValue": _cache[0] || (_cache[0] = id => (_unref(userOpts).roleId = id))
              }, null, 8, ["model-value", "items", "loading", "disabled", "readonly", "clearable", "error-messages"])
            ]),
            _: 1
          }),
          _createVNode(_component_v_col, {
            cols: "12",
            md: "6"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_text_field, {
                modelValue: _unref(userOpts).email,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((_unref(userOpts).email) = $event)),
                label: "Email*",
                type: "email",
                readonly: !!_ctx.user,
                "error-messages": getErrorMessages(_unref(validator).email.$errors)
              }, null, 8, ["modelValue", "readonly", "error-messages"])
            ]),
            _: 1
          }),
          _createVNode(_component_v_col, {
            cols: "12",
            md: "6"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_text_field, {
                modelValue: _unref(userOpts).surname,
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((_unref(userOpts).surname) = $event)),
                label: "Фамилия*",
                "error-messages": getErrorMessages(_unref(validator).surname.$errors)
              }, null, 8, ["modelValue", "error-messages"])
            ]),
            _: 1
          }),
          _createVNode(_component_v_col, {
            cols: "12",
            md: "6"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_text_field, {
                modelValue: _unref(userOpts).name,
                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((_unref(userOpts).name) = $event)),
                label: "Имя*",
                "error-messages": getErrorMessages(_unref(validator).name.$errors)
              }, null, 8, ["modelValue", "error-messages"])
            ]),
            _: 1
          }),
          _createVNode(_component_v_col, {
            cols: "12",
            md: "6"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_text_field, {
                modelValue: _unref(userOpts).patronymic,
                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((_unref(userOpts).patronymic) = $event)),
                label: "Отчество"
              }, null, 8, ["modelValue"])
            ]),
            _: 1
          }),
          _createVNode(_component_v_col, {
            cols: "12",
            md: "6"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(VPhoneField), {
                phone: _unref(userOpts).phone,
                "onUpdate:phone": _cache[5] || (_cache[5] = ($event: any) => ((_unref(userOpts).phone) = $event)),
                label: "Телефон",
                "error-messages": getErrorMessages(_unref(validator).phone.$errors)
              }, null, 8, ["phone", "error-messages"])
            ]),
            _: 1
          }),
          _createVNode(_component_v_col, {
            cols: "12",
            md: 12
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(VPasswordField), {
                modelValue: _unref(userOpts).password,
                "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((_unref(userOpts).password) = $event)),
                label: _ctx.user ? 'Пароль' : 'Пароль*',
                "error-messages": getErrorMessages(_unref(validator).password.$errors)
              }, null, 8, ["modelValue", "label", "error-messages"])
            ]),
            _: 1
          }),
          _createVNode(_component_v_col, {
            cols: "12",
            md: visibleCardIssuePointSelect.value ? 6 : 12
          }, {
            default: _withCtx(() => [
              (!selectedAdminRole.value)
                ? (_openBlock(), _createBlock(_unref(VMunicipalEntitiesSelect), {
                    key: 0,
                    modelValue: _unref(userOpts).municipalEntityId,
                    "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((_unref(userOpts).municipalEntityId) = $event)),
                    "municipal-entities-service": _ctx.municipalEntitiesService,
                    "error-messages": getErrorMessages(_unref(validator).municipalEntityId.$errors),
                    disabled: loading.value || _ctx.disabled,
                    readonly: hasMeManage.value && !hasManage.value,
                    label: "Муниципальное образование*"
                  }, null, 8, ["modelValue", "municipal-entities-service", "error-messages", "disabled", "readonly"]))
                : _createCommentVNode("", true)
            ]),
            _: 1
          }, 8, ["md"]),
          (visibleCardIssuePointSelect.value)
            ? (_openBlock(), _createBlock(_component_v_col, {
                key: 0,
                cols: "12",
                md: "6"
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(VCardIssuePointSelect), {
                    modelValue: _unref(userOpts).cardIssuePointId,
                    "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((_unref(userOpts).cardIssuePointId) = $event)),
                    "card-issue-points-service": _ctx.cardIssuePointsService,
                    "municipal-entities-service": _ctx.municipalEntitiesService,
                    "additional-filter-data": { municipalEntityId: _unref(userOpts).municipalEntityId },
                    "error-messages": getErrorMessages(_unref(validator).cardIssuePointId.$errors),
                    disabled: loading.value || _ctx.disabled,
                    readonly: !_unref(userOpts).municipalEntityId,
                    label: "Пункт выдачи карт*"
                  }, null, 8, ["modelValue", "card-issue-points-service", "municipal-entities-service", "additional-filter-data", "error-messages", "disabled", "readonly"])
                ]),
                _: 1
              }))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }),
      _createVNode(_component_v_row, { justify: "end" }, {
        default: _withCtx(() => [
          (_ctx.user && (hasManage.value || hasMeManage.value))
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                _createVNode(_component_v_spacer),
                _createVNode(_component_v_col, { cols: "auto" }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_btn, {
                      loading: loading.value,
                      disabled: _ctx.disabled,
                      color: "red-accent-4",
                      variant: "outlined",
                      onClick: _cache[9] || (_cache[9] = ($event: any) => (deleteConfirmation.value = true))
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(" Удалить ")
                      ]),
                      _: 1
                    }, 8, ["loading", "disabled"])
                  ]),
                  _: 1
                }),
                _createVNode(_unref(DeleteConfirmationDialog), {
                  modelValue: deleteConfirmation.value,
                  "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((deleteConfirmation).value = $event)),
                  onAgree: _cache[11] || (_cache[11] = 
            () => {
              emit('delete', _ctx.user?.id)
              deleteConfirmation.value = false
            }
          )
                }, {
                  title: _withCtx(() => [
                    _createTextVNode(" Подтвердите действие ")
                  ]),
                  _: 1
                }, 8, ["modelValue"])
              ], 64))
            : _createCommentVNode("", true),
          _createVNode(_component_v_col, { cols: "auto" }, {
            default: _withCtx(() => [
              _createVNode(_component_v_btn, {
                type: "submit",
                loading: loading.value,
                disabled: _ctx.disabled
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.user ? 'Сохранить' : 'Создать'), 1)
                ]),
                _: 1
              }, 8, ["loading", "disabled"])
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["onSubmit", "disabled"]))
}
}

})